import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { NARROW_QUERY } from './layoutStyles';
import { PageFrame } from './PageFrame';
import { PaneModeProvider } from './PaneContext';
import { WS_FRAME } from './tokens';

export interface RegistryLayoutProps {
  /** `<PageHeader />`. Locked. */
  header: ReactNode;
  /** `<Tabs />`, optional. Locked. */
  tabs?: ReactNode;
  /** `<FilterBar />`, optional. Locked. */
  filters?: ReactNode;
  /** The data region: normally `<RegistryTable />`. The only thing that scrolls. */
  children: ReactNode;
  /** `<DetailPane />`, optional. Scrolls on its own. */
  pane?: ReactNode;
  /**
   * `viewport` (default): measure the space left in the window so the page never scrolls.
   * `parent`: fill a parent that already has a definite height: storyboard frames, dialogs.
   */
  fill?: 'viewport' | 'parent';
  /** Pane width in px. */
  paneWidth?: number;
  /**
   * Narrow layouts show the record in a Sheet instead of the pane. The module owns when it is
   * open (normally: a row was chosen) so a record selected at load does not cover the table.
   */
  paneOpen?: boolean;
  onPaneClose?: () => void;
}

/**
 * LAYOUT 2: Registry (DESIGN.md): locked title, tabs and filter bar, then a data region with an
 * optional detail pane. A module chooses this layout and supplies its parts; it does not draw
 * its own page frame.
 *
 * Built on `PageFrame`, so the locked rows, the measured height and the narrow rule are the same
 * as every other layout. At 899px of layout width or less the pane becomes a Sheet (`paneOpen`,
 * `onPaneClose`) and the table takes the full width.
 */
export function RegistryLayout({
  header,
  tabs,
  filters,
  children,
  pane,
  fill = 'viewport',
  paneWidth = WS_FRAME.paneWidth,
  paneOpen = false,
  onPaneClose = () => {},
}: RegistryLayoutProps) {
  return (
    <PageFrame header={header} tabs={tabs} toolbar={filters} fill={fill}>
      {({ narrow }) => (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'grid',
            gap: 4,
            gridTemplateColumns: pane ? `minmax(0, 1fr) ${paneWidth}px` : 'minmax(0, 1fr)',
            gridTemplateRows: 'minmax(0, 1fr)',
            // Before the first measurement the pane is hidden by this query; after it,
            // `DetailPane` renders itself as a Sheet.
            [NARROW_QUERY]: {
              gridTemplateColumns: 'minmax(0, 1fr)',
              '& > aside': { display: 'none' },
            },
          }}
        >
          <Box sx={{ display: 'flex', minHeight: 0, minWidth: 0 }}>{children}</Box>
          <PaneModeProvider value={{ narrow, open: paneOpen, onClose: onPaneClose }}>
            {pane}
          </PaneModeProvider>
        </Box>
      )}
    </PageFrame>
  );
}
